import React, { useEffect, useState } from "react";
import {
  Input,
  Button,
  Layout,
  Popover,
  Select,
  Table,
  Skeleton,
  Pagination,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  FaSearch,
  FaFilter,
  FaEye,
  FaEdit,
  FaPlus,
  FaFolderOpen,
} from "react-icons/fa";
import { Get } from "../../config/api/get";
import { ASSETS } from "../../config/constants/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ImageUrl } from "../../helpers/Functions";
import "../course-management/course-management.css";

const ASSET_TYPES = [
  { value: "IMAGE", label: "IMAGE" },
  { value: "VIDEO", label: "VIDEO" },
  { value: "PDF", label: "PDF" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

function AssetManagement() {
  const token = useSelector((state) => state.user.userToken);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });
  const [filter, setFilter] = useState({
    keyword: "",
    type: undefined,
    status: undefined,
  });

  const startIndex =
    (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const recordsMessage = `Showing records ${endIndex} of ${paginationConfig.totalDocs}`;

  const fetchAssets = async (page, limit, keyword, type, status) => {
    setLoading(true);
    try {
      const queryObj = {
        page: String(page || paginationConfig.pageNumber),
        limit: String(limit || paginationConfig.limit),
      };
      if (keyword) queryObj.keyword = keyword;
      if (type) queryObj.type = type;
      if (status) queryObj.status = status;

      const res = await Get(ASSETS.getAssets, token, queryObj);
      const data = res?.data ?? res;
      if (data?.docs) {
        setAssets(data.docs);
        setPaginationConfig({
          pageNumber: data.page ?? page ?? 1,
          limit: data.limit ?? limit ?? 10,
          totalDocs: data.totalDocs ?? 0,
          totalPages: data.totalPages ?? 0,
        });
      } else {
        message.error(data?.message || "Failed to load assets");
      }
    } catch (e) {
      message.error(e?.message || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets(
      paginationConfig.pageNumber,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: page }));
    fetchAssets(
      page,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const handleLimitChange = (limit) => {
    setPaginationConfig((prev) => ({ ...prev, limit, pageNumber: 1 }));
    fetchAssets(1, limit, filter.keyword || undefined, filter.type, filter.status);
  };

  const handleSearch = () => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(
      1,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const handleFilterApply = () => {
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(
      1,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const resetFilter = () => {
    setFilter({ keyword: "", type: undefined, status: undefined });
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(1, paginationConfig.limit);
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      render: (_, __, index) => (
        <span className="cm-index">
          {(paginationConfig.pageNumber - 1) * paginationConfig.limit +
            index +
            1}
        </span>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title) => (
        <span className="cm-title-cell" title={title}>
          {title}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => <span className="cm-code">{type || "—"}</span>,
    },
    {
      title: "Courses",
      key: "courses",
      ellipsis: true,
      render: (_, record) => {
        const courses = record?.courses || [];
        if (!Array.isArray(courses) || courses.length === 0) {
          return <span className="cm-category-cell">—</span>;
        }
        const text =
          courses
            .map((c) => (typeof c === "object" ? c?.title : c))
            .filter(Boolean)
            .join(", ") || "—";
        return (
          <span className="cm-category-cell" title={text}>
            {text}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <span
          className="cm-meta-cell"
          style={{ textTransform: "uppercase" }}
        >
          {status ?? "—"}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (
        <span className="cm-meta-cell">
          {date ? dayjs(date).format("M/D/YYYY") : "—"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="cm-view-btn"
            aria-label="View asset"
            onClick={() =>
              navigate(`/asset-management/${record._id}`, {
                state: { asset: record },
              })
            }
          >
            <FaEye aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cm-view-btn"
            aria-label="Edit asset"
            onClick={() =>
              navigate(`/asset-management/${record._id}/edit`, {
                state: { asset: record },
              })
            }
          >
            <FaEdit aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  const filterContent = (
    <div className="filterDropdown cm-filter-popover">
      <p className="cm-filter-head">Filter assets</p>
      <hr />
      <div className="filterDropdownBody">
        <p className="mainLabel">Type</p>
        <Select
          size="large"
          className="filterSelectBox cm-filter-select"
          placeholder="Select Type"
          value={filter.type}
          onChange={(v) => setFilter((f) => ({ ...f, type: v }))}
          allowClear
          options={ASSET_TYPES}
        />
        <p className="mainLabel">Status</p>
        <Select
          size="large"
          className="filterSelectBox cm-filter-select"
          placeholder="Select Status"
          value={filter.status}
          onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
          allowClear
          options={STATUS_OPTIONS}
        />
        <Button
          type="primary"
          block
          size="large"
          className="loginButton cm-filter-apply"
          onClick={handleFilterApply}
        >
          Apply
        </Button>
        <Button
          type="primary"
          block
          size="large"
          className="loginButton"
          onClick={resetFilter}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return (
        <button type="button" className="cm-pager-btn">
          Previous
        </button>
      );
    }
    if (type === "next") {
      return (
        <button type="button" className="cm-pager-btn">
          Next
        </button>
      );
    }
    return originalElement;
  };

  return (
    <Layout className="cm-page">
      <section
        className="cm-hero"
        style={{
          backgroundImage: `url(${ImageUrl("coursesbnr.png")})`,
        }}
      >
        <div className="cm-hero-overlay" aria-hidden />
        <div className="cm-wrap cm-hero-inner">
          <div className="cm-hero-copy">
            <span className="cm-hero-badge">RESOURCE LIBRARY</span>
            <h1 className="fontFamily1 cm-hero-title">
              Manage Assets. <span>Empower Delivery.</span>
            </h1>
            <p className="fontFamily1 cm-hero-sub">
              Store and organize images, videos and documents for your training
              content.
            </p>
            <div className="cm-hero-actions">
              <Button
                type="primary"
                size="large"
                className="cm-hero-btn-primary"
                onClick={() => navigate("/asset-management/add")}
              >
                <span className="cm-hero-btn-plus">
                  <FaPlus />
                </span>
                Add Asset
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="cm-wrap cm-main">
        <div className="cm-panel">
          <div className="cm-toolbar">
            <div className="cm-toolbar-intro">
              <span className="cm-toolbar-icon" aria-hidden>
                <FaFolderOpen />
              </span>
              <h2 className="fontFamily1">Resource Library</h2>
            </div>
            <div className="cm-toolbar-actions">
              <Button
                type="primary"
                className="cm-btn-course"
                onClick={() => navigate("/asset-management/add")}
              >
                Add Asset
              </Button>
            </div>
          </div>

          <div className="cm-sub-toolbar">
            <div className="cm-page-size">
              <span>Show</span>
              <Select
                size="large"
                className="cm-limit-select"
                value={paginationConfig.limit}
                onChange={handleLimitChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                  { value: 50, label: "50" },
                ]}
              />
              <span>entries</span>
            </div>
            <div className="cm-search-row">
              <Popover
                content={filterContent}
                trigger="click"
                open={filterOpen}
                onOpenChange={setFilterOpen}
                placement="bottomRight"
                arrow={false}
              >
                <Button className="cm-filter-trigger" aria-label="Open filters">
                  <FaFilter />
                </Button>
              </Popover>
              <div className="cm-search-wrap">
                <Input
                  className="cm-search"
                  placeholder="Search by title…"
                  value={filter.keyword}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, keyword: e.target.value }))
                  }
                  onPressEnter={handleSearch}
                />
                <button
                  type="button"
                  className="cm-search-btn"
                  aria-label="Search assets"
                  onClick={handleSearch}
                >
                  <FaSearch />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="cm-skeleton">
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          ) : (
            <div className="cm-table-wrap">
              <Table
                className="cm-table"
                dataSource={assets}
                columns={columns}
                rowKey="_id"
                pagination={false}
                scroll={{ x: "max-content" }}
              />
            </div>
          )}

          <div className="cm-pagination-row">
            <p className="fontFamily1 cm-pagination-meta">{recordsMessage}</p>
            <Pagination
              className="styledPagination cm-pagination"
              onChange={handlePageChange}
              current={paginationConfig.pageNumber}
              pageSize={paginationConfig.limit}
              total={paginationConfig.totalDocs}
              itemRender={itemRender}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AssetManagement;
